/**
 * Modal — for a decision that must be made before anything else continues.
 * Used mostly as a confirmation step for irreversible desk actions.
 */
import { Show, children, createEffect, onCleanup, type JSX, type ParentProps } from "solid-js";
import { Portal } from "solid-js/web";
import { cn } from "./utils/cn";
import { Button } from "./button";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  footer?: JSX.Element;
  width?: "default" | "wide";
}

export function Modal(props: ParentProps<ModalProps>): JSX.Element {
  createEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  return (
    <Show when={props.open}>
      <ModalBody {...props} />
    </Show>
  );
}

function ModalBody(props: ParentProps<ModalProps>): JSX.Element {
  const footer = children(() => props.footer);
  return (
    <Portal>
      <div
        class="fixed inset-0 flex items-end justify-center sm:items-center sm:p-400"
        style={{ "z-index": "var(--c9-z-modal)" }}
      >
        <div
          class="absolute inset-0 bg-[color:var(--c9-blanket)]"
          onClick={() => props.onClose()}
          aria-hidden="true"
        />
        <div
          role="dialog"
          aria-modal="true"
          class={cn(
            "relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-[1rem] sm:rounded-large",
            "border border-[color:var(--c9-border)] bg-[var(--c9-surface)] shadow-[var(--c9-shadow-overlay)]",
            props.width === "wide" ? "sm:max-w-[42rem]" : "sm:max-w-[28rem]",
          )}
        >
          <header class="shrink-0 px-300 pt-300">
            <h2 class="text-h-s font-[670] text-[color:var(--c9-text)]">{props.title}</h2>
            <Show when={props.description}>
              <p class="mt-100 text-body text-[color:var(--c9-text-subtle)]">{props.description}</p>
            </Show>
          </header>

          <div class="min-h-0 flex-1 overflow-y-auto px-300 py-250">{props.children}</div>

          <footer class="flex shrink-0 items-center justify-end gap-150 px-300 pb-300">
            <Show when={footer()} fallback={<Button onClick={() => props.onClose()}>Close</Button>}>
              {footer()}
            </Show>
          </footer>
        </div>
      </div>
    </Portal>
  );
}
